/**
 * Tabs.jsx
 * Created by Michael Stanaland on 6/21/18.
 * based on previous Tabs implementation by Destin Frasier on 7/24/17.
 */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Slider from './Slider';

/**
 * Renders a tab list with buttons, followed by the active tab panel.
 *
 * If the tab list does not fit in the wrapping container, the list becomes
 * horizontally scrollable and arrow(s) are added to each end to indicate which
 * direction(s) have more content.
 *
 * Basic usage
 * ===================================================================
 *
 * import Tabs from './sharedComponents/Tabs';
 * import Panel from './sharedComponents/Panel';
 *
 * <Tabs>
 *   <Panel label="Tab one">
 *     <div>Panel contents</div>
 *   </Panel>
 *   <Panel label="Tab two">
 *     <div>Panel contents</div>
 *   </Panel>
 * </Tabs>
 *
 *
 * Controlled Tabs
 * ===================================================================
 * Tabs becomes a controlled component if the activeIndex prop is not
 * null and a number. To function correctly, you must pass a number to
 * activeIndex and a function to onChange.
 *
 * state = { activeIndex: 0 };
 * handleTabChange = (activeIndex) => {
 *   this.setState({ activeIndex })
 * }
 *
 * <Tabs activeIndex={this.state.activeIndex} onChange={this.handleTabChange}>
 *   <Panel label="Tab one">
 *     <div>Panel contents</div>
 *   </Panel>
 *   <Panel label="Tab two">
 *     <div>Panel contents</div>
 *   </Panel>
 * </Tabs>
 */

const propTypes = {
    defaultActiveIndex: PropTypes.number,
    activeIndex: PropTypes.number,
    children: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.element
    ]).isRequired,
    selectedIndexChanged: PropTypes.func,
    onChange: PropTypes.func,
    indentTabs: PropTypes.bool,
    className: PropTypes.string
};

const defaultProps = {
    defaultActiveIndex: 0,
    activeIndex: null,
    indentTabs: false
};

export default class Tabs extends React.Component {
    constructor(props) {
        super(props);

        const { activeIndex, defaultActiveIndex } = props;
        const isControlled = activeIndex != null && typeof activeIndex === 'number';

        this.state = {
            activeIndex: isControlled ? activeIndex : defaultActiveIndex
        };

        this.renderTabButtons = this.renderTabButtons.bind(this);
        this.renderTabPanels = this.renderTabPanels.bind(this);
        this.handleTabClick = this.handleTabClick.bind(this);
    }

    isControlled() {
        const { activeIndex } = this.props;
        return activeIndex != null && typeof activeIndex === 'number';
    }

    handleTabClick(e, activeIndex) {
        e.preventDefault();
        if (this.isControlled()) {
            // If controlled, send index to onChange callback
            this.props.onChange(activeIndex);
        }
        else {
            // If not controlled, set activeIndex and invoke
            // selectedIndexChanged callback
            this.setState({ activeIndex }, () => {
                if (this.props.selectedIndexChanged) {
                    this.props.selectedIndexChanged(activeIndex);
                }
            });
        }
    }

    renderTabButtons() {
        return React.Children
            .map(this.props.children, (child, index) => {
                const tabID = index;
                const selected = this.isControlled() ? tabID === this.props.activeIndex : tabID === this.state.activeIndex;
                const className = selected ? 'rec-tab rec-tab-active' : 'rec-tab';

                if (!child.props['data-hide']) {
                    return (
                        <li
                            key={`tab-${tabID}`}
                            className={className}
                            role="presentation">
                            <button
                                role="tab"
                                aria-selected={selected}
                                onClick={(event) => this.handleTabClick(event, index)}>
                                {child.props.label}
                            </button>
                        </li>
                    );
                }

                return null;
            });
    }

    renderTabPanels() {
        return React.Children
            .map(this.props.children, (child, index) => {
                const tabID = index;
                const selected = this.isControlled() ? tabID === this.props.activeIndex : tabID === this.state.activeIndex;

                let hidden = 'hide';
                if (selected) {
                    hidden = '';
                }

                return (
                    <section
                        {...child.props}
                        key={`tab-panel-${tabID}`}
                        role="tabpanel"
                        aria-hidden={!selected}
                        className={hidden}>
                        {child.props.children}
                    </section>
                );
            });
    }

    render() {
        const { indentTabs, className } = this.props;

        const classes = cx("rec-tab-list", {
            "rec-tab-indent": indentTabs,
            [className]: className
        });

        return (
            <div className={classes} data-component="Tabs">
                <ul
                    ref={(listHolder) => {
                        this.listHolder = listHolder;
                    }}
                    className="rec-tabs"
                    role="tablist">
                    <Slider>
                        {this.renderTabButtons()}
                    </Slider>
                </ul>

                {this.renderTabPanels()}
            </div>
        );
    }
}

Tabs.propTypes = propTypes;
Tabs.defaultProps = defaultProps;
